import {Property} from "@/src/modules/properties/types";

import {getStorageItem} from "../lib/storage";
import {applyActiveShiftPrimaryProperty, consumePendingActiveShiftPrimaryPropertySync} from "../lib/apply-active-shift-primary-property";
import {useFiltersStore} from "../store/filters";
import {useScopedFiltersStore} from "../store/scoped-filters";
import {useUserPropertyStore} from "../store/user-property";
import {Me} from "../types";
import {useShiftsStore} from "@/src/modules/shifts/store";

const USER_PROPERTY_STORE_KEY = "user-property-store";

function getPersistedUserProperty(): Property | null {
  try {
    const raw = getStorageItem(USER_PROPERTY_STORE_KEY, false);
    if (!raw || typeof raw !== "string") return null;
    const parsed = JSON.parse(raw) as {state?: {userProperty?: Property}};
    return parsed?.state?.userProperty ?? null;
  } catch {
    return null;
  }
}

/**
 * Retry persisted read on next tick for transient failures (e.g. react-secure-storage
 * init, hydration race). Calls onResolved with the property if found on retry.
 */
function retryPersistedRead(onResolved: (property: Property) => void, me: Me): void {
  queueMicrotask(() => {
    const persisted = getPersistedUserProperty();
    if (!persisted?.id) return;
    if (me.properties.some((p) => String(p.id) === String(persisted.id))) {
      onResolved(persisted);
    }
  });
}

function isPropertyInMe(property: Property | null, me: Me): boolean {
  if (!property?.id) return false;
  return me.properties.some((p) => String(p.id) === String(property.id));
}

function tryApplyPendingActiveShiftPrimary(me: Me): boolean {
  const activeShift = useShiftsStore.getState().activeShift;
  if (!activeShift) {
    return false;
  }

  return (
    consumePendingActiveShiftPrimaryPropertySync(activeShift.shift_id) &&
    applyActiveShiftPrimaryProperty(activeShift, me.properties)
  );
}

class UserPropertyService {
  clearUserProperty() {
    useUserPropertyStore.getState().clearUserProperty();
  }

  initUserProperty(me: Me) {
    if (!me.properties.length) {
      useUserPropertyStore.getState().setUserProperty(null);
      return;
    }

    if (me.isResident) {
      const current = useUserPropertyStore.getState().userProperty;
      if (current && isPropertyInMe(current, me)) {
        const fresh = me.properties.find((p) => String(p.id) === String(current.id))!;
        useUserPropertyStore.getState().setUserProperty(fresh);
      }
      return;
    }

    const storeProperty = useUserPropertyStore.getState().userProperty;
    const persistedProperty = getPersistedUserProperty();
    const userProperty = persistedProperty ?? storeProperty;

    const isUserPropertyValid = isPropertyInMe(userProperty, me);

    if (!isUserPropertyValid) {
      useUserPropertyStore.getState().setUserProperty(me.properties[0]);
      retryPersistedRead(
        (property) => {
          if (!isPropertyInMe(property, me)) return;
          const latest = useUserPropertyStore.getState().userProperty;
          if (String(latest?.id) === String(property.id)) return;
          useUserPropertyStore.getState().setUserProperty(property);
        },
        me,
      );
      tryApplyPendingActiveShiftPrimary(me);
      return;
    }

    const property = me.properties.find((p) => String(p.id) === String(userProperty!.id))!;
    // Always refresh from /me so sections, rules, and contacts stay current (persisted store can be stale).
    useUserPropertyStore.getState().setUserProperty(property);
    tryApplyPendingActiveShiftPrimary(me);
  }

  setUserProperty(property: Property) {
    useFiltersStore.getState().clearFilters();
    useScopedFiltersStore.getState().clearAllScopeFilters();
    useUserPropertyStore.getState().setUserProperty(property);
  }

  getMaxSafelistingsInARow(): number {
    const userProperty = useUserPropertyStore.getState().userProperty;
    if (!userProperty) return 0;
    const rule = userProperty.rules.find((r) => r.key === "days_in_a_row");
    if (!rule) return 0;
    return rule.value as number;
  }
}

export const userPropertyService = new UserPropertyService();
